import React, { useState } from 'react';
import LegendaryWeapons from '../components/project/LegendaryWeapons';
import CyclingImages from '../components/project/CyclingImages';
import SectionHeader from '../components/SectionHeader';
import { renderParagraphs } from '../utils/textFormatters';
import ProjectBanner from '../components/project/ProjectBanner';
import ToggleableDetails from '../components/ToggleableDetails';
import ContributionToggleDetails from '../components/ContributionToggleDetails';

const HopliteProjectPage = ({ project, showTrivia }) => {
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Function to handle image clicks
  const handleImageClick = (imageSrc) => {
    if (enlargedImage === imageSrc) {
      setEnlargedImage(null); // Shrink if clicked again
    } else {
      setEnlargedImage(imageSrc); // Enlarge if clicked first time
    }
  };

  return (
    <div className="project-page">
      {/* Use the ProjectBanner component instead of inline banner */}
      <ProjectBanner project={project} />
      
      <div className="max-w-5xl mx-auto p-8">
        {/* Overview Section */}
        <SectionHeader title="Overview" />
        <div className="mb-16">
          <h3 className="text-2xl font-bold mb-4 text-[#294C6F] dark:text-[#4f9cf9]">{project.titleIntro}</h3>
          <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
            {/* First part of overview */}
            {project.overview1 
              ? renderParagraphs(project.overview1)
              : renderParagraphs(project.overview && project.overview.slice(0, 6))}
          </div>
          
          <h3 className="text-2xl font-bold mb-4 mt-8 text-[#294C6F] dark:text-[#4f9cf9]">What I Contributed:</h3>
          <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
            {/* Second part of overview */}
            {project.overview2 
              ? renderParagraphs(project.overview2)
              : renderParagraphs(project.overview && project.overview.slice(6))}
          </div>
        </div>

        {/* Next Preview Text (if available) */}
        {project.nextPreview && (
          <div className="mb-8 text-xl text-[#294C6F] dark:text-[#4f9cf9] font-medium italic">
            {project.nextPreview}
          </div>
        )}

        {/* Legendary Weapons Section */}
        {project.legendaryWeapons && (
          <LegendaryWeapons 
            weapons={project.legendaryWeapons}
            contributionOrder={project.legendaryWeapons.contributionOrder}
            showTrivia={showTrivia}
          />
        )}

        {/* Champions Section */}
        {project.champions && project.champions.season1 && (
          <div className="mb-16">
            <SectionHeader title="Hoplite Champions Season 1" />
            
            {/* First line of description above the video */}
            <p className="mb-4 text-lg text-[#333333] dark:text-white">
              {project.champions.season1.description[0]}
            </p>
            
            <div className="aspect-video w-full md:w-[70%] mx-auto mb-8">
              <iframe
                className="w-full h-full rounded-xl"
                src={project.champions.season1.videoUrl}
                title="Hoplite Champions Season 1"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            {/* Main description */}
            <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
              {renderParagraphs(project.champions.season1.description.slice(1).filter(line => line.trim() !== "" && line.trim() !== " "))}
            </div>
            
            {/* Implementation Details with ToggleableDetails */}
            {project.champions.season1.implementationDetails && (
              <ToggleableDetails 
                title="Problems we had to solve" 
                paragraphs={project.champions.season1.implementationDetails}
              />
            )}
            
            {/* Post-Description as normal paragraph */}
            {project.champions.season1.postDescription && (
              <div className="mt-6 text-lg text-[#333333] dark:text-white leading-relaxed">
                {renderParagraphs(project.champions.season1.postDescription)}
              </div>
            )}
          </div>
        )}

        {/* World Generation Section */}
        {project.worldGeneration && (
          <div className="mb-16">
            <SectionHeader title="World Generation" />
            
            {/* First line of description above the images */}
            <p className="mb-4 text-lg text-[#333333] dark:text-white">
              {project.worldGeneration.description[0]}
            </p>
            
            {/* Cycling Images for World Generation */}
            <div className="mb-2">
              <CyclingImages images={project.worldGeneration.progressImages} />
            </div>
            
            {/* GitHub Repository Link */}
            <div className="mb-6 text-center">
              <a 
                href="https://github.com/Invictable/TerraPackBuilderDemo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#2978C8] hover:text-[#294C6F] dark:text-[#4f9cf9] dark:hover:text-[#7fb5fa] transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.302 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.236 1.239 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                View TerraPackBuilderDemo on GitHub
              </a>
            </div>
            
            {/* Main description (first part) */}
            <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
              {renderParagraphs(project.worldGeneration.description.slice(2, 12))}
            </div>
            
            {/* Implementation Details with ToggleableDetails */}
            {project.worldGeneration.implementationDetails && (
              <ToggleableDetails 
                title="Generation System Details" 
                paragraphs={project.worldGeneration.implementationDetails}
              />
            )}
            
            {/* Post-Description as normal paragraph */}
            {project.worldGeneration.postDescription && (
              <div className="mt-6 text-lg text-[#333333] dark:text-white leading-relaxed">
                {renderParagraphs(project.worldGeneration.postDescription)}
              </div>
            )}
          </div>
        )}

        {/* Level Design Section */}
        {project.levelDesignPreText && (
          <div className="mb-16">
            <SectionHeader title="Level Design" />
            <p className="mb-4 text-[#333333] dark:text-white">{project.levelDesignPreText}</p>
            
            {/* Citadel Structure - Image on left, text on right */}
            {project.citadel && (
              <div className="flex flex-col md:flex-row mb-12 gap-8">
                <div className="w-full md:w-1/2">
                  <div className="flex flex-col gap-4">
                    {project.citadel.images && project.citadel.images.map((image, index) => (
                      <div 
                        key={index} 
                        className={`cursor-pointer transition-all duration-300 ${
                          enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                        }`}
                        onClick={() => handleImageClick(`${process.env.PUBLIC_URL}${image}`)}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}${image}`}
                          alt={`Citadel ${index + 1}`}
                          className={`rounded-lg ${
                            enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  {showTrivia && project.citadel.trivia && (
                    <p className="mt-4 text-sm italic text-[#294C6F] dark:text-[#4f9cf9]">{project.citadel.trivia}</p>
                  )}
                </div>
                <div className="w-full md:w-1/2 flex flex-col justify-start">
                  <h3 className="text-2xl font-bold mb-4 text-[#294C6F] dark:text-[#4f9cf9] mt-0">Citadel Nether Structure</h3>
                  <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
                    {renderParagraphs(project.citadel.description)}
                  </div>
                </div>
              </div>
            )}
            
            {/* Deathmatch Arena - Text on left, image on right */}
            {project.deathmatch && (
              <div className="flex flex-col md:flex-row-reverse mb-12 gap-8">
                <div className="w-full md:w-1/2">
                  <div className="flex flex-col gap-4">
                    {project.deathmatch.images && project.deathmatch.images.map((image, index) => (
                      <div 
                        key={index} 
                        className={`cursor-pointer transition-all duration-300 ${
                          enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                        }`}
                        onClick={() => handleImageClick(`${process.env.PUBLIC_URL}${image}`)}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}${image}`}
                          alt={`Deathmatch Arena ${index + 1}`}
                          className={`rounded-lg ${
                            enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Hardcoded note that always displays */}
                  <p className="mt-4 text-sm italic text-[#294C6F] dark:text-[#4f9cf9]">*this map has since been changed and no longer features an open center or increased chest spawns</p>
                </div>
                <div className="w-full md:w-1/2 flex flex-col justify-start">
                  <h3 className="text-2xl font-bold mb-4 text-[#294C6F] dark:text-[#4f9cf9] mt-0">Acropolis Deathmatch Arena</h3>
                  <div className="text-lg text-[#333333] dark:text-white leading-relaxed">
                    {renderParagraphs(project.deathmatch.description)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Extra Section - As its own separate section */}
        {project.extra && (
          <div className="mb-16">
            <SectionHeader title="Other Notable Contributions" />
            <div className="mb-8">
              <div className="flex flex-col gap-4 mb-6">
                {project.extra.images && project.extra.images.map((image, index) => (
                  <div 
                    key={index} 
                    className={`cursor-pointer transition-all duration-300 ${
                      enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' : ''
                    }`}
                    onClick={() => handleImageClick(`${process.env.PUBLIC_URL}${image}`)}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}${image}`}
                      alt={`Extra ${index + 1}`}
                      className={`rounded-lg ${
                        enlargedImage === `${process.env.PUBLIC_URL}${image}` ? 'max-h-[90vh] max-w-[90vw] object-contain' : 'w-full h-auto'
                      }`}
                    />
                  </div>
                ))}
              </div>
              
              {/* Introduction paragraph */}
              {project.extra.description && project.extra.description[0] && (
                <p className="text-lg text-[#333333] dark:text-white mb-6">{project.extra.description[0]}</p>
              )}
              
              {/* Contribution cards with toggleable details */}
              <div className="space-y-6">
                {project.extra.description && project.extra.description.slice(1).map((item, index) => {
                  // Check if this is a main point (doesn't start with "-")
                  if (!item.trim().startsWith("-")) {
                    // Find the related bullet points (items that follow and start with "-")
                    const bulletPoints = [];
                    let i = index + 1;
                    while (
                      project.extra.description[i + 1] && 
                      project.extra.description[i + 1].trim().startsWith("-")
                    ) {
                      bulletPoints.push(project.extra.description[i + 1].replace(/^-\s*/, ''));
                      i++;
                    }
                    
                    return (
                      <div key={index} className="mb-6">
                        {bulletPoints.length > 0 ? (
                          <ContributionToggleDetails
                            title={item}
                            paragraphs={bulletPoints}
                          />
                        ) : (
                          <h3 className="text-xl font-medium text-[#222222] dark:text-white mb-3">{item}</h3>
                        )}
                      </div>
                    );
                  }
                  // Skip bullet points as they're handled with their main points
                  return null;
                }).filter(Boolean)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HopliteProjectPage; 